/**
 * Utility function that extracts and returns a specific component content from a given object
 * based on the provided __typename. If the object is undefined or does not match the specified
 * __typename, the function returns null.
 *
 * @template ComponentType - A type constraint that extends an object with an optional
 * _typename property or undefined or null. This represents the object type we're inspecting.
 * @template TypeName - A string type representing the __typename we want to match against.
 *  This type is constrained to match only valid __typename values found in ComponentType,
 *  providing a stricter type check.
 *
 * @param {ComponentType} component - The object which may contain the __typename property
 * we're interested in.
 * @param {TypeName} desiredTypeName - The __typename value to match against the object's
 * __typename.
 *
 * @returns {Extract<NonNullable<ComponentType>, Record<'__typename', TypeName>> | null}
 * - The extracted component content if the __typename matches and the object is not undefined,
 *  otherwise null.
 */
export const componentByTypename = <
  ComponentType extends { __typename?: string } | undefined | null,
  TypeName extends string &
  (ComponentType extends { __typename: infer ActualTypeName } ? ActualTypeName : never),
>(
    component: ComponentType,
    desiredTypeName: TypeName,
  // Check if the component is not undefined and if its __typename matches the desiredTypeName.
  // If so, cast the component to the specific matched type and return it.
  // Otherwise, return null to indicate the absence of a matching component.
  ): Extract<NonNullable<ComponentType>, Record<'__typename', TypeName>> | null =>
    (component && component.__typename === desiredTypeName
      ? (component as Extract<NonNullable<ComponentType>, Record<'__typename', TypeName>>)
      : null);
